/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Account-recovery restore flow (V3 mnemonic). Mounted from the auth
// welcome screen via "Forgot your devices?". Single-screen, two-stage
// form: identifier → OTP + 12-word recovery phrase → finalize OTP.
//
// Identical staging to the legacy passphrase restore screen — only the
// secret input changes from a single-line passphrase field to a
// multi-line mnemonic textarea, and the `onRestore` payload carries
// `mnemonic` instead of `passphrase`. The two OTP stages (recover/verify
// then a fresh verify) are unchanged.

import { useColorScheme } from 'nativewind';
import { useState } from 'react';
import { ActivityIndicator, TextInput, View } from 'react-native';

import { tokens } from '../../design-system/tokens';
import {
  AuthField,
  AuthHeader,
  AuthInput,
  AuthLayout,
  AuthLinkButton,
  AuthPrimaryButton,
} from '../AuthLayout';

export interface RecoveryMnemonicRestoreScreenProps {
  readonly onSendCode: (identifier: string) => Promise<void>;
  readonly onRestore: (input: {
    code: string;
    mnemonic: string;
  }) => Promise<void>;
  /**
   * Optional. When provided, the screen renders a third 'finalize'
   * step after `onRestore` resolves: a one-time-code input for the
   * fresh OTP the host requested via the implicit second
   * `/v1/auth/start`. When omitted, `onRestore` resolving is terminal.
   */
  readonly onFinalize?: (input: { code: string }) => Promise<void>;
  readonly onBack: () => void;
}

export function RecoveryMnemonicRestoreScreen({
  onSendCode,
  onRestore,
  onFinalize,
  onBack,
}: RecoveryMnemonicRestoreScreenProps): JSX.Element {
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [mnemonic, setMnemonic] = useState('');
  const [finalizeCode, setFinalizeCode] = useState('');
  const [step, setStep] = useState<'identifier' | 'restore' | 'finalize'>('identifier');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSend = async (): Promise<void> => {
    if (!identifier.trim()) {
      setError('Enter the email or phone tied to your account.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      await onSendCode(identifier.trim());
      setStep('restore');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPending(false);
    }
  };

  const handleRestore = async (): Promise<void> => {
    if (!code.trim() || !mnemonic.trim()) {
      setError('Enter the one-time code and your 12-word recovery phrase.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      await onRestore({ code: code.trim(), mnemonic: mnemonic.trim() });
      if (onFinalize !== undefined) {
        setStep('finalize');
        setCode('');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPending(false);
    }
  };

  const handleFinalize = async (): Promise<void> => {
    if (onFinalize === undefined) return;
    if (!finalizeCode.trim()) {
      setError('Enter the new one-time code.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      await onFinalize({ code: finalizeCode.trim() });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPending(false);
    }
  };

  const subtitle =
    step === 'identifier'
      ? "We'll send a one-time code to verify it's you. Your recovery phrase decrypts the backup locally — Ping never sees it."
      : step === 'restore'
        ? 'Enter the code we just sent and the 12-word recovery phrase you saved when you signed up.'
        : "Almost done — we sent a fresh code to finish signing this device in.";

  return (
    <AuthLayout accessibilityLabel="restore-from-backup">
      <AuthHeader title="Restore from backup" subtitle={subtitle} />

      {step === 'identifier' && (
        <View className="flex-col">
          <AuthField label="Email or phone number" error={error}>
            <AuthInput
              value={identifier}
              onChangeText={(next) => {
                setIdentifier(next);
                if (error !== null) setError(null);
              }}
              placeholder="Enter your email or phone number"
              autoCapitalize="none"
              hasError={Boolean(error)}
              accessibilityLabel="ping_recovery_identifier"
            />
          </AuthField>

          <View className="h-xl" />

          <AuthPrimaryButton
            onPress={() => {
              void handleSend();
            }}
            disabled={pending || identifier.trim().length === 0}
            accessibilityLabel="ping_recovery_send_code"
          >
            {pending ? <ActivityIndicator /> : 'Send code'}
          </AuthPrimaryButton>
        </View>
      )}

      {step === 'restore' && (
        <View className="flex-col">
          <AuthField label="One-time code">
            <AuthInput
              value={code}
              onChangeText={(next) => {
                setCode(next);
                if (error !== null) setError(null);
              }}
              placeholder="123456"
              autoCapitalize="none"
              keyboardType="number-pad"
              maxLength={6}
              textContentType="oneTimeCode"
              accessibilityLabel="ping_recovery_code"
            />
          </AuthField>

          <View className="h-md" />

          <AuthField label="Recovery phrase" error={error}>
            <MnemonicTextarea
              value={mnemonic}
              onChangeText={(next) => {
                setMnemonic(next);
                if (error !== null) setError(null);
              }}
              hasError={Boolean(error)}
            />
          </AuthField>

          <View className="h-xl" />

          <AuthPrimaryButton
            onPress={() => {
              void handleRestore();
            }}
            disabled={pending || code.trim().length === 0 || mnemonic.trim().length === 0}
            accessibilityLabel="ping_recovery_restore"
          >
            {pending ? <ActivityIndicator /> : 'Restore'}
          </AuthPrimaryButton>

          <View className="items-center mt-md">
            <AuthLinkButton
              fontSize={14}
              onPress={() => {
                setStep('identifier');
                setCode('');
                setError(null);
              }}
              accessibilityLabel="ping_recovery_resend"
            >
              Didn't get a code? Resend
            </AuthLinkButton>
          </View>
        </View>
      )}

      {step === 'finalize' && (
        <View className="flex-col">
          <AuthField label="One-time code" error={error}>
            <AuthInput
              value={finalizeCode}
              onChangeText={(next) => {
                setFinalizeCode(next);
                if (error !== null) setError(null);
              }}
              placeholder="123456"
              autoCapitalize="none"
              keyboardType="number-pad"
              maxLength={6}
              textContentType="oneTimeCode"
              hasError={Boolean(error)}
              accessibilityLabel="ping_recovery_finalize_code"
            />
          </AuthField>

          <View className="h-xl" />

          <AuthPrimaryButton
            onPress={() => {
              void handleFinalize();
            }}
            disabled={pending || finalizeCode.trim().length === 0}
            accessibilityLabel="ping_recovery_finalize"
          >
            {pending ? <ActivityIndicator /> : 'Finish sign-in'}
          </AuthPrimaryButton>
        </View>
      )}

      <View className="items-center mt-xl">
        <AuthLinkButton
          fontSize={14}
          onPress={onBack}
          accessibilityLabel="ping_recovery_back"
        >
          Back to sign in
        </AuthLinkButton>
      </View>
    </AuthLayout>
  );
}

// ── Multi-line mnemonic input ──────────────────────────────────────

interface MnemonicTextareaProps {
  readonly value: string;
  readonly onChangeText: (next: string) => void;
  readonly hasError?: boolean;
}

function MnemonicTextarea({
  value,
  onChangeText,
  hasError,
}: MnemonicTextareaProps): JSX.Element {
  const { colorScheme: scheme } = useColorScheme();
  const placeholderTextColor =
    tokens.color.semantic[scheme === 'dark' ? 'dark' : 'light'].text.secondary;

  const borderClass = hasError
    ? 'border-light-accent-rose dark:border-dark-accent-rose'
    : 'border-light-border-subtle dark:border-dark-border-subtle focus:border-light-accent-brand dark:focus:border-dark-accent-brand';

  return (
    <View
      className={`w-full min-h-[88px] rounded-[8px] border ${borderClass} bg-light-surface-panel dark:bg-dark-surface-panel px-md py-sm`}
    >
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder="Enter your 12-word recovery phrase, separated by spaces"
        placeholderTextColor={placeholderTextColor}
        multiline
        numberOfLines={3}
        autoCapitalize="none"
        autoCorrect={false}
        autoComplete="off"
        accessibilityLabel="ping_recovery_mnemonic"
        {...({ enableFocusRing: false } as { enableFocusRing: boolean })}
        className="flex-1 font-mono font-normal text-[15px] leading-[22px] text-light-text-primary dark:text-dark-text-primary outline-none"
        style={{ padding: 0, textAlignVertical: 'top' }}
      />
    </View>
  );
}
